import { useState, useEffect } from "react";
import { servers as serversList } from "@/server/crons/server-monitoring/servers";
import { useStatusRandomizer } from "./useStatusRandomizer";
import { useWebSocket } from "@/hooks/useWebsocket";
import type { ServerStatusUpdate } from "@/server/crons/server-monitoring/types";

const initialMockStatuses = {
  "Tracking Server": { status: "warning", responseTime: "320ms", uptime: "98.2%", color: "bg-yellow-500", bgColorStyle: "rgba(234, 179, 8, 0.2)" },
  "Google Analytics Server": { status: "offline", responseTime: "N/A", uptime: "45.1%", color: "bg-red-500", bgColorStyle: "rgba(239, 68, 68, 0.2)" },
};

const statusPriority = { offline: 0, warning: 1, online: 2 };

type MockStatus = {
  status: string;
  responseTime: string;
  uptime: string;
  color: string;
  bgColorStyle: string;
  size?: string;
  lastUpdate?: number;
};

type MockStatuses = Record<string, MockStatus>;

function getStatusColor(status: string): { color: string; bgColorStyle: string } {
  switch (status) {
    case 'online':
      return { color: 'bg-green-500', bgColorStyle: 'rgba(34, 197, 94, 0.2)' };
    case 'warning':
      return { color: 'bg-yellow-500', bgColorStyle: 'rgba(234, 179, 8, 0.2)' };
    case 'offline':
      return { color: 'bg-red-500', bgColorStyle: 'rgba(239, 68, 68, 0.2)' };
    default:
      return { color: 'bg-green-500', bgColorStyle: 'rgba(34, 197, 94, 0.2)' };
  }
}

export function useServerStatus(randomize?: number) {
  const [mockStatuses, setMockStatuses] = useState<MockStatuses>(initialMockStatuses as MockStatuses);

  useStatusRandomizer(randomize, setMockStatuses);

  const { lastMessage } = useWebSocket({
    onMessage: (message) => {
      if (message.type === 'server-status') {
        const update = message as ServerStatusUpdate;
        const { color, bgColorStyle } = getStatusColor(update.status);
        
        const sizeDisplay = update.sizeBytes !== undefined
          ? update.sizeBytes >= 1024
            ? `${Math.round(update.sizeBytes / 1024)}kb`
            : `${update.sizeBytes}b`
          : 'N/A';
        
        setMockStatuses((prev) => ({
          ...prev,
          [update.server]: {
            status: update.status,
            responseTime: `${update.responseTime}ms`,
            uptime: '99.9%',
            color,
            bgColorStyle,
            size: sizeDisplay,
            lastUpdate: Date.now(),
          },
        }));
      }
    },
  });

  const serverEntries = Object.entries(serversList)
    .filter(([_, config]) => !config.silent)
    .map(([name, config]) => {
    const url = config.url;
    const mock = mockStatuses[name as keyof typeof mockStatuses];
    const status = mock?.status || "online";
    const color = mock?.color || "bg-green-500";
    const responseTime = mock?.responseTime || "45ms";
    const uptime = mock?.uptime || "99.9%";
    const bgColorStyle = mock?.bgColorStyle || "rgba(34, 197, 94, 0.2)";
    const size = mock?.size;
    const lastUpdate = mock?.lastUpdate;

    return { name, url, status, color, responseTime, uptime, bgColorStyle, size, lastUpdate };
  });

  const servers = [...serverEntries].sort((a, b) => {
    if (a.name === "Pertento Runtime Script") return -1;
    if (b.name === "Pertento Runtime Script") return 1;
    return statusPriority[a.status as keyof typeof statusPriority] - statusPriority[b.status as keyof typeof statusPriority];
  });

  return { servers };
}
